import React, { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import DataTable from "react-data-table-component";
import { Alert, Col, FormGroup, Input, Label, Row, Spinner } from "reactstrap";
import { Breadcrumbs } from "../../../AbstractElements";
import { fetchBranches } from "../../../api/users";
import { fetchChartOfAccounts, fetchFinancialPeriods, fetchGeneralLedger } from "../../../api/accounting";
import { formatDate, formatTZS } from "../../../utils/format";
import AccountingFilterApplyButton from "./AccountingFilterApplyButton";
import { DEFAULT_TABLE_CUSTOM_STYLES } from "../Common/credoraTableUtils";
import { formatApiError, runLoad } from "../Common/credoraToast";
import { ACCOUNTING_SECTION } from "./accountingPlaceholders";
import { accountingReportBreadcrumbs } from "./accountingReportShell";
import { formatAccountType, monthStartIsoDate, todayIsoDate } from "./accountingUtils";

const GeneralLedgerPage = ({ reportMode = false }) => {
  const crumbs = accountingReportBreadcrumbs({ reportMode, title: "General Ledger" });
  const [branches, setBranches] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [branchId, setBranchId] = useState("");
  const [accountId, setAccountId] = useState("");
  const [periodId, setPeriodId] = useState("");
  const [dateFrom, setDateFrom] = useState(monthStartIsoDate());
  const [dateTo, setDateTo] = useState(todayIsoDate());
  const [lines, setLines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchBranches(true).then((rows) => {
      setBranches(rows || []);
      if (rows?.length) setBranchId(String(rows[0].id));
    });
  }, []);

  useEffect(() => {
    if (!branchId) return;
    Promise.all([
      fetchChartOfAccounts({ branch_id: branchId }),
      fetchFinancialPeriods(branchId),
    ]).then(([a, p]) => {
      setAccounts(a || []);
      setPeriods(p || []);
    });
  }, [branchId]);

  const load = useCallback(() => {
    if (!branchId) return Promise.resolve();
    setLoading(true);
    setError("");
    const params = { branch_id: branchId, limit: 500 };
    if (accountId) params.account_id = accountId;
    if (periodId) params.period_id = periodId;
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    return runLoad(() => fetchGeneralLedger(params).then(setLines), {
      errorMessage: "Failed to load general ledger.",
    })
      .catch((err) => setError(formatApiError(err, "Failed to load general ledger.")))
      .finally(() => setLoading(false));
  }, [branchId, accountId, periodId, dateFrom, dateTo]);

  useEffect(() => {
    load();
  }, [load]);

  const columns = useMemo(
    () => [
      {
        name: "Date",
        selector: (r) => r.entry_date,
        cell: (r) => formatDate(r.entry_date),
        width: "110px",
      },
      { name: "JE #", selector: (r) => r.entry_number, width: "100px" },
      { name: "Account", selector: (r) => `${r.account_code} ${r.account_name}`, grow: 2 },
      {
        name: "Type",
        selector: (r) => r.account_type,
        cell: (r) => formatAccountType(r.account_type),
        width: "100px",
      },
      { name: "Description", selector: (r) => r.line_description || r.journal_description, grow: 2 },
      {
        name: "Debit",
        selector: (r) => Number(r.debit),
        cell: (r) => (Number(r.debit) ? formatTZS(r.debit) : "—"),
        right: true,
        width: "130px",
      },
      {
        name: "Credit",
        selector: (r) => Number(r.credit),
        cell: (r) => (Number(r.credit) ? formatTZS(r.credit) : "—"),
        right: true,
        width: "130px",
      },
    ],
    []
  );

  return (
    <Fragment>
      <Breadcrumbs mainTitle={crumbs.mainTitle} parent={crumbs.parent} title={crumbs.title} />
      <div className={`container-fluid${reportMode ? " credora-finance-reports pb-5 mb-3" : ""}`}>
        {error && <Alert color="danger">{error}</Alert>}
        <div className="card mb-3">
          <div className="card-body">
            <Row className="g-3 align-items-end">
              <Col md="2">
                <FormGroup className="mb-0">
                  <Label>Branch</Label>
                  <Input type="select" value={branchId} onChange={(e) => setBranchId(e.target.value)}>
                    {branches.map((b) => (
                      <option key={b.id} value={b.id}>{b.name}</option>
                    ))}
                  </Input>
                </FormGroup>
              </Col>
              <Col md="3">
                <FormGroup className="mb-0">
                  <Label>Account</Label>
                  <Input type="select" value={accountId} onChange={(e) => setAccountId(e.target.value)}>
                    <option value="">All accounts</option>
                    {accounts.map((a) => (
                      <option key={a.id} value={a.id}>{a.code} — {a.name}</option>
                    ))}
                  </Input>
                </FormGroup>
              </Col>
              <Col md="2">
                <FormGroup className="mb-0">
                  <Label>Period</Label>
                  <Input type="select" value={periodId} onChange={(e) => setPeriodId(e.target.value)}>
                    <option value="">Any period</option>
                    {periods.map((p) => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </Input>
                </FormGroup>
              </Col>
              <Col md="2">
                <FormGroup className="mb-0">
                  <Label>From</Label>
                  <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
                </FormGroup>
              </Col>
              <Col md="2">
                <FormGroup className="mb-0">
                  <Label>To</Label>
                  <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
                </FormGroup>
              </Col>
              <AccountingFilterApplyButton onClick={load} loading={loading} />
            </Row>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h5 className="mb-0">Posted ledger lines</h5>
          </div>
          <div className="card-body p-0">
            {loading ? (
              <div className="text-center py-5"><Spinner color="primary" /></div>
            ) : (
              <DataTable columns={columns} data={lines} customStyles={DEFAULT_TABLE_CUSTOM_STYLES} pagination />
            )}
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default GeneralLedgerPage;
